import { Avatar, Popover, message } from 'antd';
import { useAuthorization } from 'components/Authorization';
import robotImg from 'assets/images/robot.png';
import { UserCurrentStatus } from './UserCurrentStatus';
import { UserPopoverContent } from './UserPopoverContent';
import { css } from '@emotion/css';
import { memo, useEffect, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useNimStatus } from 'components/Nim';
import { OnlineStatus, onlineStatus } from 'services/api/assistant.account';
import wait from 'utils/wait';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useTranslate } from '@tolgee/react';
import { isAeProduct } from 'productLine';
import { isDefaultHangUp } from 'utils/is';

const popoverCss = css`
  .ant-popover-inner-content {
    padding: 8px 10px;
  }
`;

const defaultOnlineStatus: OnlineStatus = {
  status: 'OFFLINE',
};

export const User = memo(() => {
  const { t } = useTranslate();
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const nimStatus = useNimStatus();
  const { whiteListConfig: { AE_CONVERSATION = false } = {} } = useOrgConfigStateValue();

  const name = payload?.name;
  const orgId = payload?.orgId;

  const [currentOnlineStatus, setCurrentOnlineStatus] = useState<OnlineStatus>(defaultOnlineStatus);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const hidePopover = useMemoizedFn(() => {
    setPopoverVisible(false);
  });

  useEffect(() => {
    if (nimStatus === 'Connected') {
      setCurrentOnlineStatus({ status: isDefaultHangUp(orgId) ? 'HANGUP' : 'ONLINE' });
      wait(5000)
        .then(onlineStatus)
        .then(({ data }) => {
          if (data.dataV2) {
            setCurrentOnlineStatus(data.dataV2);
          }
        })
        .catch(() => {
          message.error(t('status.fetchStatusFailed'));
        });
    } else {
      setCurrentOnlineStatus(defaultOnlineStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nimStatus]);

  return (
    <Popover
      visible={popoverVisible}
      onVisibleChange={setPopoverVisible}
      placement="rightTop"
      trigger="click"
      overlayClassName={popoverCss}
      content={
        <UserPopoverContent
          popoverVisible={popoverVisible}
          AE_CONVERSATION={AE_CONVERSATION}
          currentOnlineStatus={currentOnlineStatus}
          hidePopover={hidePopover}
          onCurrentOnlineStatus={setCurrentOnlineStatus}
        />
      }
    >
      <div className="flex flex-col gap-2 items-center" data-guide="nav-guide-step-1">
        <Avatar
          className="!bg-fill-0 !text-fill-500 !text-base"
          size={40}
          src={name ? null : robotImg}
        >
          {name?.slice(0, 2)}
        </Avatar>
        {isAeProduct && !AE_CONVERSATION ? null : (
          <UserCurrentStatus currentOnlineStatus={currentOnlineStatus} />
        )}
      </div>
    </Popover>
  );
});
